import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FLOOR_PLAN_LIMITS,
  buildSectionTree,
  collectDocumentHints,
  documentTemplate,
  flattenSectionTree,
  isImageInfoFailure,
  isRequiredTemplateKey,
  readImageInfo,
  type DocumentEntity,
  type DocumentSection,
  type LocationNode,
  type MeasurementSeries,
  type RenderedDocument,
} from '@oscom/shared';
import { ApiRequestError } from '../api/client';
import { getCustomer } from '../api/customers';
import {
  createSection,
  deleteDocument,
  deleteSection,
  documentImageUrl,
  getDocument,
  listSections,
  moveSection,
  renderDocument,
  saveDocument,
  saveSection,
  uploadDocumentImage,
} from '../api/documents';
import { listLocations } from '../api/locations';
import { listMeasurementSeries } from '../api/measurements';
import { useSession } from '../auth/SessionProvider';
import { BlockListEditor } from '../components/BlockListEditor';
import { CustomerContextNav } from '../components/CustomerContextNav';
import {
  DocumentFields,
  documentFieldsComplete,
  type DocumentFieldValues,
} from '../components/DocumentFields';
import { DocumentHints } from '../components/DocumentHints';
import { GeneratedContentView } from '../components/GeneratedContentView';
import { Layout } from '../components/Layout';
import { readFileBytes } from '../components/readFileBytes';
import { SectionTree } from '../components/SectionTree';
import { AUTOSAVE_DELAY_MS, useAutosave, type SaveStatus } from '../documents/autosave';
import {
  editorDraftText,
  fromStoredBlocks,
  toBlockInputs,
  type EditorBlock,
} from '../documents/editorBlocks';
import {
  findRenderedSection,
  nextChildOrder,
  planMove,
  sectionNumbers,
  type MoveAction,
} from '../documents/sectionTree';
import { DocumentPreviewPage } from './DocumentPreviewPage';
import { NotFoundPage } from './NotFoundPage';

/**
 * One chapter while it is edited. It carries its own customer and document,
 * so a draft still waiting when the route switches to another document is
 * saved where it belongs.
 */
interface SectionDraft {
  customerId: string;
  documentId: string;
  sectionId: string;
  title: string;
  blocks: EditorBlock[];
}

const CONFLICT_SEPARATOR = '\n\n---\n\n';
const TITLE_MISSING = 'Bitte geben Sie einen Kapiteltitel an.';
const SECTION_DELETED = 'Das Kapitel wurde zwischenzeitlich gelöscht.';

const STATUS_TEXT: Record<SaveStatus, string> = {
  idle: '',
  pending: 'Speichert …',
  saving: 'Speichert …',
  saved: 'Gespeichert',
  error: 'Nicht gespeichert',
};

function toDraft(section: DocumentSection, customerId: string, documentId: string): SectionDraft {
  return {
    customerId,
    documentId,
    sectionId: section.id,
    title: section.title,
    blocks: fromStoredBlocks(section.blocks),
  };
}

function firstInTree(sections: readonly DocumentSection[]): DocumentSection | undefined {
  return flattenSectionTree(buildSectionTree(sections))[0];
}

/**
 * The document editor (design 2026-10-31): chapter tree left, the chosen
 * chapter right, hints aside. Writers only - readers get the preview.
 */
export function DocumentEditorPage(): ReactNode {
  const { session } = useSession();
  const navigate = useNavigate();
  const { customerId = '', documentId = '' } = useParams<{
    customerId: string;
    documentId: string;
  }>();
  const me = session?.status === 'authenticated' ? session.me : undefined;
  const enabled = me?.features?.documents === true;
  const canWrite = me?.permissions.includes('documents:write') === true;
  const measurementsEnabled = me?.features?.measurements === true;

  const [customerName, setCustomerName] = useState<string | undefined>(undefined);
  const [document, setDocument] = useState<DocumentEntity | undefined>(undefined);
  const [sections, setSections] = useState<DocumentSection[]>([]);
  const [rendered, setRendered] = useState<RenderedDocument | undefined>(undefined);
  const [renderError, setRenderError] = useState<string | undefined>(undefined);
  const [renderLoading, setRenderLoading] = useState(false);
  const [locations, setLocations] = useState<LocationNode[]>([]);
  const [series, setSeries] = useState<MeasurementSeries[]>([]);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [draft, setDraft] = useState<SectionDraft | undefined>(undefined);
  const [revision, setRevision] = useState(0);
  const [conflictText, setConflictText] = useState<string | undefined>(undefined);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [treeOpen, setTreeOpen] = useState(false);
  const [settings, setSettings] = useState<DocumentFieldValues | undefined>(undefined);
  const [confirmDocumentDelete, setConfirmDocumentDelete] = useState(false);

  const draftRef = useRef<SectionDraft | undefined>(undefined);
  const etags = useRef(new Map<string, string>());
  // Last stored title per chapter: a draft with an empty title field is saved
  // under it, so its text is never lost.
  const savedTitles = useRef(new Map<string, string>());
  const files = useRef(new Map<string, File>());
  // Last own version put into the conflict box (reset on "Schließen").
  const lastOwnText = useRef<string | undefined>(undefined);
  // True while a chapter is reloaded after a conflict: input is not saved
  // then (it would carry a stale etag) but goes to the conflict box.
  const conflictReload = useRef(false);

  const rememberEtags = useCallback((list: readonly DocumentSection[]) => {
    for (const section of list) {
      if (section._etag) etags.current.set(section.id, section._etag);
      savedTitles.current.set(section.id, section.title);
    }
  }, []);

  const showDraft = useCallback((next: SectionDraft | undefined) => {
    draftRef.current = next;
    setDraft(next);
    setRevision((value) => value + 1);
  }, []);

  /**
   * Keeps the user's own text in the conflict box. A second conflict before
   * "Schließen" appends instead of replacing, so no version is ever lost.
   */
  const keepOwnVersion = useCallback((own: SectionDraft) => {
    const text = editorDraftText(own.title, own.blocks);
    if (text === lastOwnText.current) return;
    lastOwnText.current = text;
    setConflictText((previous) =>
      previous === undefined ? text : `${previous}${CONFLICT_SEPARATOR}${text}`,
    );
  }, []);

  const loadRender = useCallback(async () => {
    setRenderLoading(true);
    try {
      setRendered(await renderDocument(customerId, documentId));
      setRenderError(undefined);
    } catch (caught: unknown) {
      setRenderError(
        caught instanceof Error ? caught.message : 'Das Dokument konnte nicht aufgebaut werden.',
      );
    } finally {
      setRenderLoading(false);
    }
  }, [customerId, documentId]);

  const reloadSections = useCallback(async (): Promise<DocumentSection[]> => {
    const { sections: list } = await listSections(customerId, documentId);
    rememberEtags(list);
    setSections(list);
    return list;
  }, [customerId, documentId, rememberEtags]);

  const saveDraft = useCallback(
    async (next: SectionDraft) => {
      const etag = etags.current.get(next.sectionId);
      const saved = await saveSection(next.customerId, next.documentId, next.sectionId, {
        title: next.title.trim(),
        blocks: toBlockInputs(next.blocks),
        ...(etag ? { ifMatchEtag: etag } : {}),
      });
      rememberEtags([saved]);
      setSections((current) =>
        current.map((section) => (section.id === saved.id ? saved : section)),
      );
    },
    [rememberEtags],
  );

  const { status, queue } = useAutosave<SectionDraft>(
    AUTOSAVE_DELAY_MS,
    saveDraft,
    (error, failed) => {
      // 409: changed elsewhere; 404: deleted elsewhere. Either way the own
      // version goes to the box and the chapters are reloaded, so the user
      // is never stuck with a draft that cannot be saved.
      const deleted = error instanceof ApiRequestError && error.status === 404;
      if (error instanceof ApiRequestError && (error.status === 409 || deleted)) {
        queue.cancel();
        if (deleted) setMessage(SECTION_DELETED);
        // The newest own text of that chapter: input may have arrived while the
        // failed save was running.
        const own = draftRef.current?.sectionId === failed.sectionId ? draftRef.current : failed;
        keepOwnVersion(own);
        conflictReload.current = true;
        void reloadSections()
          .then((list) => {
            const current = draftRef.current;
            if (current?.sectionId !== failed.sectionId) return;
            // Typed during the reload (not queued, see updateDraft): keep it.
            if (current !== own) keepOwnVersion(current);
            queue.cancel();
            const fresh = list.find((section) => section.id === failed.sectionId);
            if (fresh) {
              showDraft(toDraft(fresh, failed.customerId, failed.documentId));
              return;
            }
            // The chapter is gone: open the first one that is left.
            const first = firstInTree(list);
            setSelectedId(first?.id);
            showDraft(first ? toDraft(first, failed.customerId, failed.documentId) : undefined);
            void loadRender();
          })
          .catch(() => {
            const current = draftRef.current;
            if (current?.sectionId === failed.sectionId && current !== own) {
              keepOwnVersion(current);
            }
            setMessage(
              'Das Kapitel konnte nicht neu geladen werden. Bitte laden Sie die Seite neu.',
            );
          })
          .finally(() => {
            conflictReload.current = false;
          });
        return;
      }
      setMessage(error instanceof Error ? error.message : 'Speichern fehlgeschlagen.');
    },
  );

  useEffect(() => {
    if (!enabled || !canWrite || !customerId || !documentId) return;
    let cancelled = false;
    Promise.all([
      getCustomer(customerId),
      getDocument(customerId, documentId),
      listSections(customerId, documentId),
      listLocations(customerId),
      measurementsEnabled ? listMeasurementSeries(customerId) : Promise.resolve({ series: [] }),
    ])
      .then(([customer, loadedDocument, sectionResponse, locationResponse, seriesResponse]) => {
        if (cancelled) return;
        setCustomerName(customer.name);
        setDocument(loadedDocument);
        rememberEtags(sectionResponse.sections);
        setSections(sectionResponse.sections);
        setLocations(locationResponse.locations);
        setSeries(seriesResponse.series);
        const first = firstInTree(sectionResponse.sections);
        setSelectedId(first?.id);
        showDraft(first ? toDraft(first, customerId, documentId) : undefined);
      })
      .catch((caught: unknown) => {
        if (!cancelled)
          setLoadError(
            caught instanceof Error ? caught.message : 'Das Dokument konnte nicht geladen werden.',
          );
      });
    void loadRender();
    return () => {
      cancelled = true;
      // Another document (or leaving): save what still waits, under the ids
      // the draft carries.
      void queue.flush();
    };
  }, [
    enabled,
    canWrite,
    customerId,
    documentId,
    measurementsEnabled,
    loadRender,
    rememberEtags,
    showDraft,
    queue,
  ]);

  // An empty title is never saved, so it counts as unsaved like a waiting draft.
  const titleMissing = draft !== undefined && draft.title.trim() === '';
  const shownStatus: SaveStatus = titleMissing ? 'error' : status;
  // Closing the tab while a draft waits or fails: let the browser ask first.
  const unsaved = shownStatus === 'pending' || shownStatus === 'saving' || shownStatus === 'error';
  useEffect(() => {
    if (!unsaved) return;
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // Older browsers only ask when returnValue is set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved]);

  const template = document ? documentTemplate(document.documentTypeKey) : undefined;
  const requiredIds = useMemo(
    () =>
      new Set(
        sections
          .filter((section) => isRequiredTemplateKey(template, section.templateKey))
          .map((section) => section.id),
      ),
    [sections, template],
  );
  const numbers = useMemo(() => sectionNumbers(rendered), [rendered]);
  const hints = useMemo(() => (rendered ? collectDocumentHints(rendered) : []), [rendered]);

  if (!me) return null;
  if (!enabled) return <NotFoundPage />;
  if (!canWrite) return <DocumentPreviewPage />;

  function updateDraft(patch: Partial<SectionDraft>): void {
    const current = draftRef.current;
    if (!current) return;
    const next = { ...current, ...patch };
    draftRef.current = next;
    setDraft(next);
    // Conflict reload: the input ends up in the conflict box, not the queue.
    if (conflictReload.current) return;
    // Empty title: the field stays empty (with a hint), but the text is saved
    // under the last stored title - also when the page is left via the router.
    const savedTitle = savedTitles.current.get(next.sectionId);
    if (next.title.trim() === '') {
      if (savedTitle !== undefined) queue.schedule({ ...next, title: savedTitle });
      return;
    }
    queue.schedule(next);
  }

  /** Why the current chapter may not be left right now, if at all. */
  function leaveBlockedReason(): string | undefined {
    const current = draftRef.current;
    if (!current) return undefined;
    if (current.blocks.some((block) => block.type === 'image' && block.uploading)) {
      return 'Bitte warten, bis das Bild hochgeladen ist.';
    }
    if (current.title.trim() === '') return TITLE_MISSING;
    if (conflictReload.current) return 'Bitte warten, das Kapitel wird neu geladen.';
    return undefined;
  }

  function patchImage(
    blockId: string,
    patch: Partial<Extract<EditorBlock, { type: 'image' }>>,
  ): void {
    const current = draftRef.current;
    if (!current) return;
    updateDraft({
      blocks: current.blocks.map((block) =>
        block.id === blockId && block.type === 'image' ? { ...block, ...patch } : block,
      ),
    });
  }

  async function uploadFor(blockId: string): Promise<void> {
    const file = files.current.get(blockId);
    if (!file) return;
    patchImage(blockId, { uploading: true, uploadError: undefined });
    try {
      if (file.size > FLOOR_PLAN_LIMITS.maxImageBytes)
        throw new Error('Die Datei ist zu groß (höchstens 20 MB).');
      const info = readImageInfo(await readFileBytes(file));
      if (isImageInfoFailure(info)) throw new Error(info.error);
      const result = await uploadDocumentImage(customerId, documentId, file, info.mediaType);
      files.current.delete(blockId);
      patchImage(blockId, { assetId: result.assetId, uploading: false, uploadError: undefined });
    } catch (caught: unknown) {
      patchImage(blockId, {
        uploading: false,
        uploadError: caught instanceof Error ? caught.message : 'Hochladen fehlgeschlagen.',
      });
    }
  }

  async function selectSection(sectionId: string): Promise<void> {
    if (sectionId === selectedId) return;
    const blocked = leaveBlockedReason();
    if (blocked) {
      setMessage(blocked);
      return;
    }
    if (!(await queue.flush())) return;
    const section = sections.find((entry) => entry.id === sectionId);
    setSelectedId(sectionId);
    setMessage(undefined);
    setTreeOpen(false);
    showDraft(section ? toDraft(section, customerId, documentId) : undefined);
  }

  async function structural(action: () => Promise<string | undefined>): Promise<void> {
    const blocked = leaveBlockedReason();
    if (blocked) {
      setMessage(blocked);
      return;
    }
    setBusy(true);
    setMessage(undefined);
    try {
      if (!(await queue.flush())) return;
      const select = await action();
      const list = await reloadSections();
      const keep = select ?? selectedId;
      const section = list.find((entry) => entry.id === keep) ?? firstInTree(list);
      if (section && section.id !== draftRef.current?.sectionId) {
        setSelectedId(section.id);
        showDraft(toDraft(section, customerId, documentId));
      }
      await loadRender();
    } catch (caught: unknown) {
      setMessage(caught instanceof Error ? caught.message : 'Die Änderung ist fehlgeschlagen.');
      await reloadSections().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  function addSection(parentSectionId: string | undefined): void {
    void structural(async () => {
      const created = await createSection(customerId, documentId, {
        ...(parentSectionId ? { parentSectionId } : {}),
        title: 'Neues Kapitel',
        order: nextChildOrder(sections, parentSectionId),
      });
      return created.id;
    });
  }

  function moveTo(sectionId: string, action: MoveAction): void {
    const position = planMove(sections, sectionId, action);
    if (!position) return;
    void structural(async () => {
      const etag = etags.current.get(sectionId);
      await moveSection(customerId, documentId, sectionId, {
        ...position,
        ...(etag ? { ifMatchEtag: etag } : {}),
      });
      return undefined;
    });
  }

  function removeSection(sectionId: string): void {
    void structural(async () => {
      await deleteSection(customerId, documentId, sectionId);
      return sectionId === selectedId
        ? firstInTree(sections.filter((entry) => !entry.path.includes(sectionId)))?.id
        : undefined;
    });
  }

  function openSettings(): void {
    if (!document) return;
    setSettings({
      documentTypeKey: document.documentTypeKey as DocumentFieldValues['documentTypeKey'],
      title: document.title,
      subtitle: document.subtitle ?? '',
      siteLocationId: document.siteLocationId,
      seriesIds: [...document.seriesIds],
    });
  }

  async function saveSettings(): Promise<void> {
    if (!settings || !documentFieldsComplete(settings)) return;
    try {
      const subtitle = settings.subtitle.trim();
      const saved = await saveDocument(customerId, documentId, {
        title: settings.title.trim(),
        ...(subtitle ? { subtitle } : {}),
        siteLocationId: settings.siteLocationId,
        seriesIds: settings.seriesIds,
        ...(document?._etag ? { ifMatchEtag: document._etag } : {}),
      });
      setDocument(saved);
      setSettings(undefined);
      await loadRender();
    } catch (caught: unknown) {
      setMessage(caught instanceof Error ? caught.message : 'Speichern fehlgeschlagen.');
    }
  }

  async function removeDocument(): Promise<void> {
    try {
      await deleteDocument(customerId, documentId);
      navigate(`/kunden/${encodeURIComponent(customerId)}/dokumente`);
    } catch (caught: unknown) {
      setMessage(caught instanceof Error ? caught.message : 'Löschen fehlgeschlagen.');
    }
  }

  const selectedRendered = selectedId ? findRenderedSection(rendered, selectedId) : undefined;
  const generated = selectedRendered?.generatorKind ? (
    <div className="generated-box">
      <p className="notice notice--info">Wird aus den erfassten Daten erzeugt</p>
      <GeneratedContentView
        items={selectedRendered.items}
        customerId={customerId}
        links={me.features}
      />
      {selectedRendered.children
        .filter((child) => child.source === 'generated')
        .map((child) => (
          <div key={child.id} className="generated-box__section">
            <h3>
              {child.number} {child.title}
            </h3>
            <GeneratedContentView items={child.items} customerId={customerId} links={me.features} />
            {child.children.map((grandChild) => (
              <div key={grandChild.id}>
                <h4>
                  {grandChild.number} {grandChild.title}
                </h4>
                <GeneratedContentView
                  items={grandChild.items}
                  customerId={customerId}
                  links={me.features}
                />
              </div>
            ))}
          </div>
        ))}
    </div>
  ) : null;
  const blockEditor = draft ? (
    <BlockListEditor
      blocks={draft.blocks}
      revision={revision}
      imageUrl={(assetId) => documentImageUrl(customerId, documentId, assetId)}
      onChange={(blocks) => updateDraft({ blocks })}
      onUpload={(blockId, file) => {
        files.current.set(blockId, file);
        void uploadFor(blockId);
      }}
      onRetryUpload={(blockId) => void uploadFor(blockId)}
    />
  ) : null;
  const base = `/kunden/${encodeURIComponent(customerId)}/dokumente`;

  return (
    <Layout user={{ displayName: me.displayName, role: me.role }} showLogout>
      <section className="stack">
        {customerName ? (
          <CustomerContextNav
            customerId={customerId}
            customerName={customerName}
            features={me.features}
          />
        ) : null}
        {loadError ? <p className="notice notice--warning">{loadError}</p> : null}
        <header className="doc-editor__head card">
          <h1>{document?.title ?? 'Dokument'}</h1>
          <span className="muted">
            {template?.name ?? ''}
            {rendered?.siteLabel ? ` · ${rendered.siteLabel}` : ''}
          </span>
          <Link className="button" to={`${base}/${encodeURIComponent(documentId)}/vorschau`}>
            Vorschau
          </Link>
          <button type="button" className="button button--ghost" onClick={openSettings}>
            Dokumentangaben
          </button>
          <p className="doc-editor__status" role="status" aria-live="polite">
            {STATUS_TEXT[shownStatus]}
          </p>
          {message ? (
            <p className="notice notice--warning" role="alert">
              {message}
            </p>
          ) : null}
        </header>

        {settings ? (
          <div className="card stack">
            <h2>Dokumentangaben</h2>
            <DocumentFields
              values={settings}
              onChange={setSettings}
              locations={locations}
              series={series}
              seriesListed={measurementsEnabled}
              templateFixed
            />
            <div className="plan-info__actions">
              <button
                type="button"
                className="button button--primary"
                disabled={!documentFieldsComplete(settings)}
                onClick={() => void saveSettings()}
              >
                Speichern
              </button>
              <button type="button" className="button" onClick={() => setSettings(undefined)}>
                Abbrechen
              </button>
              {confirmDocumentDelete ? (
                <button type="button" className="button" onClick={() => void removeDocument()}>
                  Dokument endgültig löschen
                </button>
              ) : (
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => setConfirmDocumentDelete(true)}
                >
                  Dokument löschen
                </button>
              )}
            </div>
          </div>
        ) : null}

        {conflictText !== undefined ? (
          <div className="notice notice--warning conflict-box" role="alert">
            <p>
              Das Kapitel wurde zwischenzeitlich geändert oder gelöscht, die Kapitel wurden neu
              geladen. Ihre Fassung zum Kopieren:
            </p>
            <textarea aria-label="Ihre Fassung" readOnly value={conflictText} />
            <button
              type="button"
              className="button"
              onClick={() => {
                lastOwnText.current = undefined;
                setConflictText(undefined);
              }}
            >
              Schließen
            </button>
          </div>
        ) : null}

        <button
          type="button"
          className="button doc-editor__toggle"
          aria-expanded={treeOpen}
          onClick={() => setTreeOpen((open) => !open)}
        >
          Kapitel
        </button>
        <div className="doc-editor">
          <div className={treeOpen ? 'doc-editor__tree card is-open' : 'doc-editor__tree card'}>
            <SectionTree
              sections={sections}
              numbers={numbers}
              selectedId={selectedId}
              requiredIds={requiredIds}
              canWrite
              busy={busy}
              onSelect={(id) => void selectSection(id)}
              onAdd={addSection}
              onMove={moveTo}
              onDelete={removeSection}
            />
          </div>
          <div className="doc-editor__main card stack">
            {draft ? (
              <>
                <div className="form-field">
                  <label htmlFor="section-title">Kapiteltitel</label>
                  <input
                    id="section-title"
                    type="text"
                    maxLength={200}
                    value={draft.title}
                    onChange={(event) => updateDraft({ title: event.target.value })}
                  />
                  {titleMissing ? <p className="muted">{TITLE_MISSING}</p> : null}
                </div>
                {selectedRendered?.blocksPlacement === 'after' ? (
                  <>
                    {generated}
                    {blockEditor}
                  </>
                ) : (
                  <>
                    {blockEditor}
                    {generated}
                  </>
                )}
              </>
            ) : (
              <p className="muted">Kein Kapitel gewählt.</p>
            )}
          </div>
          <DocumentHints
            hints={hints}
            loading={renderLoading}
            error={renderError}
            onSelect={(id) => void selectSection(id)}
            onRefresh={() => void loadRender()}
          />
        </div>
      </section>
    </Layout>
  );
}
